/**
 * OpenHouseDetailModal.jsx — rich listing preview for the Open House Planner.
 *
 * Opened from a Browse card. Shows a full-size photo carousel (Zillow photos in
 * data.photos, or the single photo_url), all facts, the listing agent +
 * brokerage + phone, the listing description, and one-tap links to the source
 * (Zillow / Redfin) where the full gallery + agent live. Photos/agent populate
 * after Zillow enrichment runs; degrades gracefully when they're absent.
 */

import { useState, useEffect } from 'react';
import {
  Box, Stack, Typography, Button, IconButton, Chip, Dialog, DialogContent,
  Divider, Tooltip,
} from '@mui/material';
import {
  Close as CloseIcon,
  ChevronLeft as PrevIcon,
  ChevronRight as NextIcon,
  OpenInNew as OpenInNewIcon,
  Phone as PhoneIcon,
  HomeWork as HouseIcon,
  Archive as ArchiveIcon,
  PlaylistAdd as PlaylistAddIcon,
} from '@mui/icons-material';
import { useFocus } from '../../hooks/useFocus';

const PT = 'America/Los_Angeles';
const VILLAGE_COLOR = '#E879F9';

function fmtPtTime(iso) {
  if (!iso) return '';
  return new Date(iso).toLocaleTimeString('en-US', { timeZone: PT, hour: 'numeric', minute: '2-digit' }).replace(':00', '');
}
function fmtWindow(s, e) {
  if (!s) return 'Time TBD';
  const a = fmtPtTime(s); if (!e) return a;
  const b = fmtPtTime(e);
  return `${a.slice(-2) === b.slice(-2) ? a.slice(0, -3) : a}–${b}`;
}
function fmtDay(iso) {
  if (!iso) return '';
  return new Date(iso).toLocaleDateString('en-US', { timeZone: PT, weekday: 'long', month: 'short', day: 'numeric' });
}
function fmtPrice(p) {
  const n = parseFloat(p);
  if (!n || isNaN(n)) return '—';
  if (n >= 1e6) return `$${(n / 1e6).toFixed(2).replace(/0$/, '').replace(/\.$/, '')}M`;
  if (n >= 1e3) return `$${Math.round(n / 1e3)}K`;
  return `$${Math.round(n)}`;
}
function isVillage(b) { return (b || '').toLowerCase().includes('village'); }

export default function OpenHouseDetailModal({ oh, open, onClose, onArchive, onAddToPlan, inPlan }) {
  const [idx, setIdx] = useState(0);
  const { isHidden } = useFocus();
  useEffect(() => { setIdx(0); }, [oh?.id]);
  if (!oh) return null;

  const photos = (Array.isArray(oh.data?.photos) && oh.data.photos.length)
    ? oh.data.photos
    : (oh.photoUrl ? [oh.photoUrl] : []);
  const hasPhotos = photos.length > 0;
  const cur = hasPhotos ? photos[Math.min(idx, photos.length - 1)] : null;
  const village = isVillage(oh.brokerage);
  const remarks = oh.data?.remarks;
  const zillowUrl = oh.data?.zillowUrl || (oh.source === 'zillow' ? oh.listingUrl : null);
  const specs = [
    oh.beds && `${parseFloat(oh.beds)} bd`,
    oh.baths && `${parseFloat(oh.baths)} ba`,
    oh.sqft && `${Number(oh.sqft).toLocaleString()} sqft`,
  ].filter(Boolean).join('  ·  ');

  const step = (d) => setIdx((i) => (i + d + photos.length) % photos.length);

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth PaperProps={{ sx: { borderRadius: 3, overflow: 'hidden' } }}>
      {/* Photo area */}
      <Box sx={{ position: 'relative', bgcolor: 'grey.900', aspectRatio: '3 / 2', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        {cur ? (
          <Box component="img" src={cur} alt={oh.address} sx={{ width: '100%', height: '100%', objectFit: 'cover' }} />
        ) : (
          <Stack alignItems="center" spacing={1} sx={{ color: 'grey.500' }}>
            <HouseIcon sx={{ fontSize: 48 }} />
            <Typography variant="caption">
              {oh.source === 'redfin' ? 'Photos load after Zillow enrichment' : 'No photo'}
            </Typography>
          </Stack>
        )}
        <IconButton onClick={onClose} sx={{ position: 'absolute', top: 8, right: 8, bgcolor: 'rgba(0,0,0,0.45)', color: '#fff', '&:hover': { bgcolor: 'rgba(0,0,0,0.65)' } }} size="small">
          <CloseIcon fontSize="small" />
        </IconButton>
        {photos.length > 1 && (
          <>
            <IconButton onClick={() => step(-1)} sx={{ position: 'absolute', left: 6, top: '50%', transform: 'translateY(-50%)', bgcolor: 'rgba(0,0,0,0.4)', color: '#fff', '&:hover': { bgcolor: 'rgba(0,0,0,0.6)' } }}><PrevIcon /></IconButton>
            <IconButton onClick={() => step(1)} sx={{ position: 'absolute', right: 6, top: '50%', transform: 'translateY(-50%)', bgcolor: 'rgba(0,0,0,0.4)', color: '#fff', '&:hover': { bgcolor: 'rgba(0,0,0,0.6)' } }}><NextIcon /></IconButton>
            <Chip size="small" label={`${idx + 1} / ${photos.length}`} sx={{ position: 'absolute', bottom: 8, right: 8, bgcolor: 'rgba(0,0,0,0.55)', color: '#fff' }} />
          </>
        )}
      </Box>

      {/* Thumbnail strip */}
      {photos.length > 1 && (
        <Stack direction="row" spacing={0.5} sx={{ p: 0.75, overflowX: 'auto', bgcolor: 'grey.100' }}>
          {photos.slice(0, 12).map((p, i) => (
            <Box key={i} component="img" src={p} alt="" onClick={() => setIdx(i)}
              sx={{ width: 56, height: 42, objectFit: 'cover', borderRadius: 0.5, cursor: 'pointer', flexShrink: 0, outline: i === idx ? '2px solid' : 'none', outlineColor: 'primary.main' }} />
          ))}
        </Stack>
      )}

      <DialogContent sx={{ p: 2 }}>
        <Stack direction="row" justifyContent="space-between" alignItems="flex-start" spacing={1}>
          <Box sx={{ minWidth: 0 }}>
            <Typography variant="h6" sx={{ fontWeight: 800, lineHeight: 1.15 }}>{oh.address}</Typography>
            <Typography variant="body2" color="text.secondary">{[oh.city, oh.zip].filter(Boolean).join(', ')}</Typography>
          </Box>
          <Typography variant="h6" sx={{ fontWeight: 800, flexShrink: 0 }}>{fmtPrice(oh.price)}</Typography>
        </Stack>

        <Stack direction="row" spacing={1} sx={{ mt: 1, flexWrap: 'wrap', alignItems: 'center' }}>
          <Chip size="small" color="primary" variant="outlined" label={`${fmtDay(oh.startAt)} · ${fmtWindow(oh.startAt, oh.endAt)}`} />
          {specs && <Typography variant="body2" color="text.secondary">{specs}</Typography>}
        </Stack>

        {/* Agent / brokerage */}
        <Divider sx={{ my: 1.5 }} />
        {(oh.agentName || oh.brokerage) ? (
          <Stack direction="row" justifyContent="space-between" alignItems="center" spacing={1}>
            <Box sx={{ minWidth: 0 }}>
              <Typography variant="caption" color="text.secondary">Listing agent</Typography>
              <Typography variant="subtitle1" sx={{ fontWeight: 700, lineHeight: 1.1 }}>{oh.agentName || 'Unknown'}</Typography>
              {oh.brokerage && (
                <Chip size="small" label={oh.brokerage} sx={{ mt: 0.5, height: 20, ...(village ? { bgcolor: VILLAGE_COLOR, color: '#fff', fontWeight: 600 } : {}) }} />
              )}
              {oh.contactId && <Chip size="small" color="success" variant="outlined" label="In CRM" sx={{ mt: 0.5, ml: 0.5, height: 20 }} />}
            </Box>
            {oh.agentPhone && (
              <Button size="small" variant="outlined" startIcon={<PhoneIcon />} href={`tel:${oh.agentPhone}`}>{oh.agentPhone}</Button>
            )}
          </Stack>
        ) : (
          <Typography variant="body2" color="text.secondary">
            Agent not captured yet{oh.source === 'redfin' ? ' — Redfin hides it. Run Zillow enrichment or open the listing below.' : '.'}
          </Typography>
        )}

        {/* Description */}
        {remarks && (
          <>
            <Divider sx={{ my: 1.5 }} />
            <Typography variant="body2" color="text.secondary" sx={{ display: '-webkit-box', WebkitLineClamp: 5, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}>{remarks}</Typography>
          </>
        )}

        {/* Actions */}
        <Divider sx={{ my: 1.5 }} />
        <Stack direction="row" spacing={1} sx={{ flexWrap: 'wrap', rowGap: 1 }}>
          {onAddToPlan && (
            <Button size="small" variant="contained" startIcon={<PlaylistAddIcon />} onClick={() => onAddToPlan(oh)} disabled={inPlan}>
              {inPlan ? 'In plan' : 'Add to plan'}
            </Button>
          )}
          {zillowUrl && (
            <Button size="small" variant="outlined" endIcon={<OpenInNewIcon />} href={zillowUrl} target="_blank" rel="noopener noreferrer">Zillow</Button>
          )}
          {oh.listingUrl && oh.listingUrl !== zillowUrl && (
            <Button size="small" variant="outlined" endIcon={<OpenInNewIcon />} href={oh.listingUrl} target="_blank" rel="noopener noreferrer">
              {oh.source === 'redfin' ? 'Redfin' : 'Listing'}
            </Button>
          )}
          {!zillowUrl && (
            <Button size="small" variant="text" endIcon={<OpenInNewIcon />}
              href={`https://www.zillow.com/homes/${encodeURIComponent([oh.address, oh.city, 'CA'].filter(Boolean).join(' '))}_rb/`}
              target="_blank" rel="noopener noreferrer">Search Zillow</Button>
          )}
          <Box sx={{ flex: 1 }} />
          {onArchive && !isHidden('openhouse.browseMore') && (
            <Tooltip title="Archive (hide from list)">
              <IconButton size="small" data-focus="openhouse.browseMore" onClick={() => { onArchive(oh); onClose(); }}><ArchiveIcon fontSize="small" /></IconButton>
            </Tooltip>
          )}
        </Stack>
      </DialogContent>
    </Dialog>
  );
}
